import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { getPayments, refundPayment } from "../lib/api.ts";
import { CheckoutModal } from "../components/CheckoutModal.tsx";
import { useIsMobile } from "../lib/use-is-mobile.ts";

interface Payment {
  id: string; amount_cents: number; currency: string; status: string; kind: string;
  method: string; description: string | null; customer_name: string | null;
  created_at: string; stripe_payment_intent_id: string | null;
}

function money(cents: number, currency: string) {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: (currency || "usd").toUpperCase() }).format(cents / 100);
}

export default function Payments() {
  const { slug } = useParams<{ slug: string }>();
  const [rows, setRows] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const load = useCallback(() => {
    if (!slug) return;
    setLoading(true);
    getPayments(slug).then((d) => setRows(d as Payment[])).catch((e: Error) => setError(e.message)).finally(() => setLoading(false));
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  async function onRefund(id: string) {
    if (!slug) return;
    setBusy(id); setError(null);
    try { await refundPayment(slug, id); load(); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(null); }
  }

  const isMobile = useIsMobile();

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
        <h1 style={{ fontSize: "1.6rem", fontWeight: 600 }}>Payments</h1>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="btn-primary" onClick={() => setCheckoutOpen(true)}>
            Collect payment
          </button>
        </div>
      </div>
      {error && <div className="alert-error" style={{ marginBottom: 16 }}>{error}</div>}
      {loading ? (
        <div style={{ padding: "48px 0", textAlign: "center", color: "var(--text-muted)" }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="zd-empty"><h3>No payments yet.</h3><p>Collect your first payment with the button above.</p></div>
      ) : isMobile ? (
        <div className="zd-card-list">
          {rows.map((p) => (
            <div key={p.id} className="zd-card">
              <div className="zd-card-row">
                <span style={{ fontWeight: 600, fontSize: 15 }}>{money(p.amount_cents, p.currency)}</span>
                <span className={`badge-${p.status === "paid" ? "green" : p.status === "refunded" ? "blue" : p.status === "failed" ? "red" : "gray"}`}>{p.status}</span>
              </div>
              <div className="zd-card-row" style={{ fontSize: 13, color: "var(--ink-soft)" }}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.customer_name ?? "—"}</span>
                <span style={{ whiteSpace: "nowrap" }}>{new Date(p.created_at).toLocaleDateString()}</span>
              </div>
              {p.status === "paid" && (
                <div className="zd-card-actions">
                  <button type="button" className="btn-secondary" disabled={busy === p.id} onClick={() => onRefund(p.id)}>
                    {busy === p.id ? "…" : "Refund"}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ textAlign: "left", color: "var(--text-muted)", fontSize: 12 }}>
            <th style={{ padding: "8px 12px" }}>Date</th><th style={{ padding: "8px 12px" }}>Amount</th>
            <th style={{ padding: "8px 12px" }}>Customer</th><th style={{ padding: "8px 12px" }}>Status</th>
            <th style={{ padding: "8px 12px" }}></th>
          </tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} style={{ borderTop: "1px solid var(--border)" }}>
                <td style={{ padding: "10px 12px", fontSize: 13 }}>{new Date(p.created_at).toLocaleDateString()}</td>
                <td style={{ padding: "10px 12px", fontWeight: 600 }}>{money(p.amount_cents, p.currency)}</td>
                <td style={{ padding: "10px 12px", fontSize: 13 }}>{p.customer_name ?? <span style={{ color: "var(--text-muted)" }}>—</span>}</td>
                <td style={{ padding: "10px 12px" }}>
                  <span className={`badge-${p.status === "paid" ? "green" : p.status === "refunded" ? "blue" : p.status === "failed" ? "red" : "gray"}`}>{p.status}</span>
                </td>
                <td style={{ padding: "10px 12px", textAlign: "right" }}>
                  {p.status === "paid" && (
                    <button type="button" className="btn-secondary" style={{ fontSize: 12.5 }} disabled={busy === p.id} onClick={() => onRefund(p.id)}>
                      {busy === p.id ? "…" : "Refund"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
      {checkoutOpen && slug && (
        <CheckoutModal
          slug={slug}
          standalone
          onClose={() => setCheckoutOpen(false)}
          onDone={() => { load(); }}
        />
      )}
    </div>
  );
}
